import { Link } from "react-router-dom";
import { Heart, GraduationCap, Tv, Sparkles, Users, MapPin } from "lucide-react";

// Add uploaded summer photos here as { src, alt } and videos as { src, poster, name }
const PHOTOS = [];
const VIDEOS = [];

const HIGHLIGHTS = [
  { icon: Users, title: "3 Camp Locations", text: "We ran summer camps at Fulton County Library, the East Point Recreation Center, and the Augusta Boys & Girls Club — serving over 200 kids." },
  { icon: Sparkles, title: "Microsoft & Best Buy Tech Camp", text: "Our tech camp at Fulton County Library, powered by Microsoft and Best Buy, gave kids hands-on experience with real technology." },
  { icon: Tv, title: "Featured on Channel 12 News", text: "Augusta's WRDW News 12 spotlighted LP Foundation and our summer program's impact on the community." },
];

export default function SummerCamp() {
  return (
    <div data-testid="summer-camp-page">
      <section className="bg-brandnavy">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Summer Camp 2026</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight text-white max-w-3xl">
            A summer of growth, discovery & fun.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            This summer, LP Foundation brought hands-on SEAA enrichment — science, engineering, athletics, and arts — to
            over <strong className="text-white">200 kids</strong> across three locations: the Microsoft &amp; Best Buy tech
            camp at Fulton County Library, the East Point Recreation Center, and the Augusta Boys &amp; Girls Club. And
            Augusta's News 12 was there to capture the impact.
          </p>
          <div className="mt-6 flex items-center gap-2 text-slate-300">
            <MapPin className="h-4 w-4 text-accent" /> Fulton County Library · East Point Rec Center · Augusta Boys &amp; Girls Club
          </div>
        </div>
      </section>

      {/* IMPACT STATS */}
      <section className="bg-accent">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
          <div className="grid gap-8 text-center sm:grid-cols-3">
            <div data-testid="stat-kids">
              <div className="font-display text-5xl font-black text-white">200+</div>
              <div className="mt-2 text-sm font-semibold uppercase tracking-wide text-orange-50">Kids Served</div>
            </div>
            <div data-testid="stat-locations">
              <div className="font-display text-5xl font-black text-white">3</div>
              <div className="mt-2 text-sm font-semibold uppercase tracking-wide text-orange-50">Camp Locations</div>
            </div>
            <div data-testid="stat-partners">
              <div className="font-display text-5xl font-black text-white">Microsoft · Best Buy</div>
              <div className="mt-2 text-sm font-semibold uppercase tracking-wide text-orange-50">Tech Camp Partners</div>
            </div>
          </div>
          <p className="mt-8 text-center text-sm text-orange-50/90 max-w-3xl mx-auto leading-relaxed">
            From the Microsoft &amp; Best Buy tech camp at Fulton County Library, to the East Point Recreation Center, to
            the Augusta Boys &amp; Girls Club — we brought hands-on SEAA enrichment to over 200 young people this summer.
          </p>
        </div>
      </section>

      {/* FEATURED NEWS INTERVIEW */}
      <section className="mx-auto max-w-5xl px-5 sm:px-8 py-16">
        <div className="flex items-center gap-2 text-accent">
          <Tv className="h-5 w-5" />
          <span className="text-xs font-semibold tracking-[0.2em] uppercase">As Seen On Channel 12 News</span>
        </div>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl font-black text-brandnavy">
          Our Summer Program, featured on WRDW News 12
        </h2>
        <div className="mt-6 overflow-hidden rounded-3xl border border-slate-100 shadow-lg bg-black">
          <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
            <iframe
              data-testid="news-interview-video"
              src="https://www.youtube.com/embed/QfYCyA1Itt8"
              title="LP Foundation on Channel 12 News Augusta — Summer Program"
              className="absolute inset-0 h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
        <p className="mt-4 text-sm text-slate-500">
          LP Foundation featured on Augusta's News 12, highlighting our summer program with the Augusta Boys &amp; Girls Club.
        </p>
      </section>

      {/* HIGHLIGHTS */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <h2 className="font-display text-3xl font-black text-brandnavy text-center">Summer Highlights</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {HIGHLIGHTS.map((h) => (
              <div key={h.title} className="rounded-3xl border border-slate-100 bg-white p-7 shadow-sm">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-orange-50 text-accent">
                  <h.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-4 font-display text-xl font-bold text-brandnavy">{h.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{h.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO GALLERY (populated as videos are uploaded) */}
      {VIDEOS.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
          <h2 className="font-display text-3xl font-black text-brandnavy">Camp Videos</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            {VIDEOS.map((v, i) => (
              <div key={i} className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
                <video controls preload="metadata" poster={v.poster} className="w-full aspect-video bg-black">
                  <source src={v.src} type="video/mp4" />
                </video>
                {v.name && <div className="p-4 font-semibold text-brandnavy">{v.name}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* PHOTO GALLERY (populated as photos are uploaded) */}
      {PHOTOS.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
          <h2 className="font-display text-3xl font-black text-brandnavy">Camp Gallery</h2>
          <div className="mt-8 grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {PHOTOS.map((p, i) => (
              <img key={i} src={p.src} alt={p.alt || "Summer camp"} loading="lazy" className="h-48 w-full rounded-2xl object-cover shadow-sm" />
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-brandnavy">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-16 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-black text-white">Want your child at camp next summer?</h2>
          <p className="mt-3 text-slate-300">Join the list, or help us bring the program to even more youth.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-4">
            <Link to="/get-involved" data-testid="summer-enroll-cta" className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-semibold text-brandnavy transition hover:-translate-y-0.5">
              <GraduationCap className="h-5 w-5" /> Enroll / Contact
            </Link>
            <Link to="/support" data-testid="summer-donate-cta" className="inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 font-semibold text-white transition hover:-translate-y-0.5 hover:bg-orange-400">
              <Heart className="h-5 w-5 fill-current" /> Donate
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
